import { downloadGeoJSON, featureCollection } from '../../common/geojson';
import { areaFeature, baselineFeature, coastlineFeatures, transectFeature } from '../utils/coastlineGeoJson';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:Shoreline
 */
const GEOJSON_MODULE = 'shoreline';

/**
 * @function buildGeoJsonExport
 * @description Gathers a finished shoreline analysis into one FeatureCollection: the area of
 * interest, the baseline, every transect with its rates, and the extracted coastline for each
 * date. Every feature says which layer it belongs to, so the file opens as one layer in a GIS and
 * splits back into four by filtering on that field - a browser can only hand over one file at a
 * time, and separate downloads would leave the user pairing them up by hand.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring, `[lng, lat]` pairs.
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {Array<Object>} [params.transectData] - Transects, in either shape.
 * @param {Array<Object>} [params.shorelineFeatures] - Extracted coastlines, already evaluated out
 *  of Earth Engine as GeoJSON features.
 * **Called by:
 * @see {CoastlineEvolutionDialog} - through the GIS export icon.
 * @memberof module:Shoreline
 * @returns {Object} A FeatureCollection, empty when the analysis has produced nothing yet.
 */
export const buildGeoJsonExport = ({ coordinates, baselineCoords, transectData, shorelineFeatures }) =>
  featureCollection([
    areaFeature(coordinates),
    baselineFeature(baselineCoords),
    (transectData || []).map(transectFeature),
    coastlineFeatures(shorelineFeatures),
  ]);

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {CoastlineEvolutionDialog}
 * @memberof module:Shoreline
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
